import { useState, useEffect } from 'react';
import {
  Plus,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Trash2,
  Eraser,
  ChevronDown,
  ChevronRight,
  MapPin,
  Minus,
  Pentagon,
  Square,
  Circle,
  Crosshair,
} from 'lucide-react';
import { useCollaborationStore } from '@/stores/collaborationStore';
import { useMapStore } from '@/stores/mapStore';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Layer, OperationalFeature, FeatureType } from '@/types/operational';

const featureIcons: Record<FeatureType, React.ElementType> = {
  point: MapPin,
  line: Minus,
  polygon: Pentagon,
  rectangle: Square,
  circle: Circle,
  ballistic: Crosshair,
};

function featureLabel(f: OperationalFeature): string {
  if (f.properties?.name) return f.properties.name;
  const type = f.properties?.featureType || 'object';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function LayerPanel() {
  const {
    ydoc,
    getLayers,
    getAnnotations,
    activeLayerId,
    setActiveLayerId,
    createLayer,
    deleteLayer,
    updateLayer,
  } = useCollaborationStore();

  const { setSelection, clearSelection, selection } = useMapStore();

  const [layers, setLayers] = useState<Layer[]>([]);
  const [features, setFeatures] = useState<OperationalFeature[]>([]);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [newLayerName, setNewLayerName] = useState('');

  useEffect(() => {
    if (!ydoc) return;
    const yLayers = getLayers();
    if (!yLayers) return;

    const updateLayers = () => {
      setLayers(yLayers.toArray().sort((a, b) => b.zIndex - a.zIndex));
    };

    updateLayers();
    yLayers.observe(updateLayers);
    return () => { yLayers.unobserve(updateLayers); };
  }, [ydoc, getLayers]);

  // Watch annotations for feature list
  useEffect(() => {
    if (!ydoc) return;
    const yAnnotations = getAnnotations();
    if (!yAnnotations) return;

    const update = () => {
      setFeatures(yAnnotations.toArray());
    };

    update();
    yAnnotations.observe(update);
    return () => { yAnnotations.unobserve(update); };
  }, [ydoc, getAnnotations]);

  const handleCreateLayer = () => {
    if (!newLayerName.trim()) return;
    createLayer(newLayerName.trim());
    setNewLayerName('');
    setIsCreating(false);
  };

  const handleToggleVisibility = (layer: Layer) => updateLayer(layer.id, { visible: !layer.visible });
  const handleToggleLock = (layer: Layer) => updateLayer(layer.id, { locked: !layer.locked });

  const handleDelete = (layer: Layer) => {
    if (layers.length <= 1) return;
    if (confirm(`Delete layer "${layer.name}"?`)) deleteLayer(layer.id);
  };

  const handleClear = (layer: Layer) => {
    if (!ydoc) return;
    const annotations = getAnnotations();
    if (!annotations) return;
    const count = features.filter(f => f.properties?.layerId === layer.id).length;
    if (count === 0) return;
    if (confirm(`Clear ${count} object(s) from "${layer.name}"?`)) {
      ydoc.transact(() => {
        const arr = annotations.toArray();
        for (let i = arr.length - 1; i >= 0; i--) {
          if (arr[i].properties?.layerId === layer.id) annotations.delete(i, 1);
        }
      });
    }
  };

  const handleDeleteFeature = (featureId: string) => {
    if (!ydoc) return;
    const annotations = getAnnotations();
    if (!annotations) return;
    ydoc.transact(() => {
      const arr = annotations.toArray();
      const idx = arr.findIndex(f => f.properties?.id === featureId);
      if (idx !== -1) annotations.delete(idx, 1);
    });
    if (selection.featureIds.includes(featureId)) {
      clearSelection();
    }
  };

  const toggleExpanded = (layerId: string) => {
    setExpandedLayers(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) next.delete(layerId);
      else next.add(layerId);
      return next;
    });
  };

  const getLayerFeatures = (layerId: string) =>
    features.filter(f => f.properties?.layerId === layerId);

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Layers
        </h3>
        <span className="text-[10px] text-muted-foreground">{layers.length}</span>
      </div>

      {/* Add layer */}
      {!isCreating ? (
        <button
          onClick={() => setIsCreating(true)}
          className="w-full flex items-center justify-center gap-1 py-1.5 mb-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-md hover:border-primary/50 transition-colors"
        >
          <Plus size={13} />
          Add Layer
        </button>
      ) : (
        <div className="flex gap-1.5 mb-2">
          <Input
            value={newLayerName}
            onChange={(e) => setNewLayerName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateLayer();
              if (e.key === 'Escape') { setIsCreating(false); setNewLayerName(''); }
            }}
            placeholder="Layer name"
            className="h-7 text-xs"
            autoFocus
          />
          <Button size="sm" className="h-7 text-xs px-2" onClick={handleCreateLayer}>
            Add
          </Button>
        </div>
      )}

      {/* Layer list */}
      <div className="space-y-0.5">
        {layers.map((layer) => {
          const layerFeatures = getLayerFeatures(layer.id);
          const isExpanded = expandedLayers.has(layer.id);

          return (
            <div key={layer.id}>
              <div
                className={cn(
                  'flex items-center gap-1 px-1.5 py-1.5 rounded-md transition-colors group',
                  activeLayerId === layer.id
                    ? 'bg-primary/10 ring-1 ring-primary/30'
                    : 'hover:bg-accent/50'
                )}
              >
                {/* Expand/collapse */}
                <button
                  onClick={() => toggleExpanded(layer.id)}
                  className="text-muted-foreground/50 hover:text-muted-foreground"
                >
                  {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>

                <div
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: layer.style.color || '#FFD700' }}
                />

                <button
                  onClick={() => setActiveLayerId(layer.id)}
                  className={cn(
                    'flex-1 text-left text-xs truncate',
                    activeLayerId === layer.id ? 'text-foreground font-medium' : 'text-muted-foreground',
                    !layer.visible && 'opacity-40'
                  )}
                >
                  {layer.name}
                </button>

                {layerFeatures.length > 0 && (
                  <span className="text-[10px] text-muted-foreground/60 mr-0.5">{layerFeatures.length}</span>
                )}

                <div className="flex items-center gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleToggleVisibility(layer)}
                    className={cn('p-1 rounded hover:bg-accent transition-colors', layer.visible ? 'text-muted-foreground' : 'text-muted-foreground/30')}
                  >
                    {layer.visible ? <Eye size={11} /> : <EyeOff size={11} />}
                  </button>
                  <button
                    onClick={() => handleToggleLock(layer)}
                    className={cn('p-1 rounded hover:bg-accent transition-colors', layer.locked ? 'text-yellow-500' : 'text-muted-foreground')}
                  >
                    {layer.locked ? <Lock size={11} /> : <Unlock size={11} />}
                  </button>
                  <button onClick={() => handleClear(layer)} className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-orange-400 transition-colors">
                    <Eraser size={11} />
                  </button>
                  <button onClick={() => handleDelete(layer)} className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>

              {/* Feature list */}
              {isExpanded && (
                <div className="ml-5 mt-0.5 space-y-px">
                  {layerFeatures.length === 0 ? (
                    <p className="text-[10px] text-muted-foreground/50 py-1 pl-2">No objects</p>
                  ) : (
                    layerFeatures.map((f) => {
                      const Icon = featureIcons[f.properties?.featureType] || MapPin;
                      const isSelected = selection.featureIds.includes(f.properties?.id || '');
                      return (
                        <div
                          key={f.properties?.id}
                          className={cn(
                            'flex items-center gap-1.5 px-2 py-1 rounded text-xs group/feat cursor-pointer transition-colors',
                            isSelected
                              ? 'bg-primary/15 text-foreground'
                              : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground'
                          )}
                          onClick={() => {
                            if (f.properties?.id) {
                              setSelection({ featureIds: [f.properties.id] });
                            }
                          }}
                          onDoubleClick={() => {
                            const map = useMapStore.getState().map;
                            if (!map || !f.geometry) return;
                            const coords = f.geometry.coordinates;
                            if (f.geometry.type === 'Point') {
                              map.flyTo({ center: coords as [number, number], zoom: 14 });
                            } else {
                              // Compute bounding box for lines/polygons
                              const flat = f.geometry.type === 'Polygon' ? coords[0] : coords;
                              let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
                              for (const c of flat as number[][]) {
                                if (c[0] < minLng) minLng = c[0];
                                if (c[0] > maxLng) maxLng = c[0];
                                if (c[1] < minLat) minLat = c[1];
                                if (c[1] > maxLat) maxLat = c[1];
                              }
                              map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, maxZoom: 16 });
                            }
                          }}
                        >
                          <Icon size={11} className="flex-shrink-0" style={{ color: f.properties?.style?.color || layer.style.color }} />
                          <span className="flex-1 truncate">{featureLabel(f)}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (f.properties?.id) handleDeleteFeature(f.properties.id);
                            }}
                            className="p-0.5 rounded opacity-0 group-hover/feat:opacity-100 hover:bg-accent text-muted-foreground hover:text-destructive transition-all"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {layers.length === 0 && (
        <p className="text-center py-6 text-xs text-muted-foreground">No layers yet</p>
      )}
    </div>
  );
}
